/**
 * CSE Confidence Calibration (CL-1 — row 0.99286).
 *
 * Coverage Self-check Engine (CSE) is the deterministic claim-extraction +
 * cross-check pipeline that lives under `src/services/siteContext/`. Its
 * output is a stream of `ExtractedClaim` records, each with an
 * `extractorConfidence` (0-100 from the Gemini Flash extractor; 0-1 from
 * authored-evidence and DM-extracted writers). Downstream surfaces map
 * the score into the canonical {low, medium, high} band via
 * `scoreToConfidence` in `types/authoredEvidence.ts`.
 *
 * Calibration motive
 * ------------------
 * Live-prod telemetry on the SC10 + UAE flows showed that the extractor
 * has been systematically over-confident on a small subset of categories
 * (e.g. `'finance.cost'`, `'commitment'`) and slightly under-confident
 * on others (`'parcel.acres'`, `'environmental.species'`). Re-prompting
 * the extractor every time we want to nudge a category is over-budget
 * (the model is Flash 2.5 and the prompt is hot-pathed), so we apply a
 * per-category multiplier as the LAST step of confidence computation.
 *
 * Default behaviour
 * -----------------
 * Every multiplier defaults to 1.0 — i.e. NO behaviour change unless an
 * admin explicitly tunes the table. The values below are baselined at
 * 1.0 today; future drift-driven updates land in `master_execution_plan`
 * row 0.99286 follow-ups with explicit rationale.
 *
 * Pipeline contract
 * -----------------
 *   raw = extractor.extractorConfidence       // 0-100 or 0-1, mixed
 *   normalised = normaliseConfidence(raw)     // → 0-1
 *   calibrated = calibrateClaimConfidence(category, normalised)
 *                                             // 0-1, calibration applied
 *
 * Callers may pass either scale; the helper is scale-aware and returns
 * the same scale it received.
 *
 * Hard rules (per CL-1 pack):
 *   - Calibration is a single multiplier per category, clamped to [0, 1]
 *     for normalised scale (or [0, 100] for the 0-100 scale).
 *   - `Number.isFinite()` strict guards on every numeric input.
 *   - Multipliers that are non-finite, negative, or absent default to 1.0
 *     (defensive — the table is hand-curated, but stale Firestore reads
 *     must not corrupt the resolver).
 */
import type { ClaimCategory } from '../../types/siteContext';

/**
 * Per-category calibration multiplier.
 *
 * Read as: `effective_confidence = raw_confidence × CSE_CONFIDENCE_CALIBRATION[category]`.
 *
 * **Default to 1.0** — NO behaviour change at ship time. Any deviation
 * MUST cite live-prod drift telemetry in the commit message.
 *
 * Categories not listed fall back to 1.0 via `getCalibrationMultiplier`.
 */
export const CSE_CONFIDENCE_CALIBRATION: Record<ClaimCategory, number> = {
  'power.grid':              1.0,
  'power.btm':               1.0,
  'power.timeline':          1.0,
  'water.cooling':           1.0,
  'water.source':            1.0,
  'water.rights':            1.0,
  'environmental.species':   1.0,
  'environmental.wetlands':  1.0,
  'environmental.nepa':      1.0,
  'environmental.tribal':    1.0,
  'environmental.phase1':    1.0,
  'regulatory.zoning':       1.0,
  'regulatory.permits':      1.0,
  'regulatory.air':          1.0,
  'geotechnical.foundation': 1.0,
  'geotechnical.seismic':    1.0,
  'parcel.boundaries':       1.0,
  'parcel.acres':            1.0,
  'connectivity.fiber':      1.0,
  'connectivity.latency':    1.0,
  'schedule.inservice':      1.0,
  'schedule.permitting':     1.0,
  'finance.cost':            1.0,
  'finance.ppa':             1.0,
  'technology.reactor':      1.0,
  'technology.cooling':      1.0,
  'technology.workload':     1.0,
  'prior-study':             1.0,
  'doc-reference':           1.0,
  'commitment':              1.0,
  'unverifiable':            1.0,
  'general':                 1.0,
};

/**
 * Returns the calibration multiplier for a category. Unknown categories
 * (stale Firestore reads, future enum entries the table doesn't know
 * about yet) return 1.0 so calibration never decreases or inflates a
 * confidence value silently.
 *
 * Non-finite stored multipliers also fall back to 1.0 — the table is
 * hand-curated, but defensive guarding keeps the resolver pinned even
 * if a future writer corrupts a value.
 */
export function getCalibrationMultiplier(category: ClaimCategory): number {
  const m = CSE_CONFIDENCE_CALIBRATION[category];
  if (!Number.isFinite(m)) return 1.0;
  if (m < 0) return 1.0;
  return m;
}

/**
 * Apply the per-category calibration to a single confidence value.
 *
 * Scale-aware: a value > 1.5 is treated as 0-100 (the SC3 extractor's
 * native scale) and clamped to `[0, 100]`; otherwise it's treated as
 * 0-1 (authored / DM-extracted writers) and clamped to `[0, 1]`.
 *
 * The 1.5 threshold matches the same normalisation rule the doc
 * checklist `requirementStatus` helper uses to bridge the two scales,
 * so calibration sits cleanly in the middle of the pipeline without
 * having to know which writer produced the claim.
 *
 * @param category         The claim category.
 * @param rawConfidence    The pre-calibration confidence. Non-finite or
 *                         negative inputs return 0 (defensive — the
 *                         caller chose to ask for calibration on a bad
 *                         value, so the answer is "no confidence").
 */
export function calibrateClaimConfidence(
  category: ClaimCategory,
  rawConfidence: number,
): number {
  if (!Number.isFinite(rawConfidence)) return 0;
  if (rawConfidence < 0) return 0;
  const multiplier = getCalibrationMultiplier(category);
  const scaled = rawConfidence * multiplier;
  // Scale-aware clamp. The 1.5 boundary matches the normalisation
  // bridge in `computeRequirementStatus`.
  if (rawConfidence > 1.5) {
    return Math.max(0, Math.min(100, scaled));
  }
  return Math.max(0, Math.min(1, scaled));
}

/**
 * Map a confidence value into the canonical {low, medium, high} band.
 *
 * Mirrors `scoreToConfidence` in `types/authoredEvidence.ts` but lives
 * here so the telemetry helper can compute tier shifts WITHOUT importing
 * authored-evidence (which would tug at the dependency graph during
 * tests). Scale-aware on the same 1.5 threshold as `calibrateClaimConfidence`.
 */
export function confidenceTier(confidence: number): 'low' | 'medium' | 'high' {
  if (!Number.isFinite(confidence)) return 'low';
  const normalised = confidence > 1.5 ? confidence / 100 : confidence;
  // Thresholds match AUTHORED_CONFIDENCE_SCORE in authoredEvidence.ts
  // (high = 0.75, medium = 0.6). Keep in lockstep with that file.
  if (normalised >= 0.75) return 'high';
  if (normalised >= 0.6)  return 'medium';
  return 'low';
}

/**
 * Telemetry — count claims whose confidence tier would shift after
 * calibration is applied. Pure function so the admin surface can call
 * it on any window of claims (24h, 7d, 30d) without holding state here.
 *
 * Returns the count of claims for which
 *   `confidenceTier(raw) !== confidenceTier(calibrated)`.
 *
 * Default multipliers (all 1.0) ⇒ this count is always 0. The KPI tile
 * surfaces this so admins can observe drift the moment a non-default
 * multiplier ships.
 */
export function countCseTierShifts(
  claims: ReadonlyArray<{ category: ClaimCategory; extractorConfidence: number }>,
): number {
  let shifted = 0;
  for (const c of claims) {
    if (!Number.isFinite(c.extractorConfidence)) continue;
    const before = confidenceTier(c.extractorConfidence);
    const after = confidenceTier(calibrateClaimConfidence(c.category, c.extractorConfidence));
    if (before !== after) shifted++;
  }
  return shifted;
}
